import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {DndProvider} from 'react-dnd';
import {NativeTypes} from 'react-dnd-html5-backend';
import {Tree, NodeModel, MultiBackend, getBackendOptions, DropOptions} from '@minoru/react-dnd-treeview';
import {CustomNode} from './CustomNode';
import {Placeholder} from './Placeholder';
import {CustomDragPreview} from './CustomDragPreview';
import {Button, Card, Col, Row, Block, Space, NoData, Modal} from '@mc/ui-v4';
import {ExternalNode} from './ExternalNode';
import {CiselnikValueModal} from '../modals/ConfigurationModal/CiselnikValueModal';
import {v4 as uuidv4} from 'uuid';
import {useForm, useFormState} from 'react-final-form';
import {AttributeConfigVO} from '../../api';

type Props = {
    data: NodeModel[];
    externalDataInit: NodeModel[];
    diskriminatorAtribut: AttributeConfigVO;
};

const TreeActions: React.FC<Props> = ({data, externalDataInit, diskriminatorAtribut}) => {
    const {change} = useForm();
    const {values} = useFormState();

    const formatedData: NodeModel[] = useMemo(() => {
        let tempNodeModel: NodeModel[] = [];
        data.forEach(item => {
            const nodeModel: NodeModel = {
                id: item.id,
                parent: item.parent ?? 0,
                text: item.text,
                droppable: item.parent === 0,
            };
            tempNodeModel.push(nodeModel);
        });

        return tempNodeModel;
    }, [data, diskriminatorAtribut]);

    const [treeData, setTreeData] = useState<NodeModel[]>(formatedData);
    const [externalData, setExternalData] = useState<NodeModel[]>(externalDataInit);

    useEffect(() => {
        // Musíme před každou změnou nastavit položky na undefined,
        // jinak formulář ignoruje změny...
        change('polozky', undefined);
        change('polozky', treeData);
    }, [JSON.stringify(treeData)]);

    const handleOrderChange = useCallback(
        (newTree: NodeModel[], options: DropOptions) => {
            const {dropTargetId, monitor, destinationIndex, dropTarget, dragSource} = options;
            const itemType = monitor.getItemType();

            if (itemType === NativeTypes.TEXT) {
                //toto jsou externi itemy

                if (dropTargetId !== 0 && dropTarget.droppable) {
                    //vkladani polozky do slozky
                    const nodeJson = monitor.getItem().text;
                    const node = JSON.parse(nodeJson);
                    node.parent = dropTargetId;
                    newTree.splice(destinationIndex, 0, node);
                    setTreeData(newTree);
                    setExternalData(externalData.filter(exnode => exnode.id !== node.id));
                }

                if (dropTargetId === 0 && destinationIndex !== 0) {
                    //vkladani polozky do slozky, pokud je kurzor POD slozkou
                    //tj. toto je zde jenom protoze testeri nechapou ze dropujou do slozky jenom kdyz maji kurzor NA ni a ne POD ni
                    const nodeJson = monitor.getItem().text;
                    const node = JSON.parse(nodeJson);
                    node.parent = newTree[destinationIndex - 1].id;
                    newTree.splice(destinationIndex, 0, node);

                    setTreeData(newTree);
                    setExternalData(externalData.filter(exnode => exnode.id !== node.id));
                }
                //toto jsou itemy uz ve stromu
            } else if (dragSource?.droppable && dropTargetId === 0) {
                //presouvame slozku v ramci prvni urovne
                setTreeData(newTree);
            } else if (!dragSource?.droppable) {
                //presouvame atributy
                if (dropTargetId !== 0 && dropTarget.droppable) {
                    //atribut přesouvám do složky
                    setTreeData(newTree);
                } else if (destinationIndex !== 0) {
                    //vkladani atributu do slozky, pokud je kurzor POD slozkou
                    //tj. toto je zde jenom protoze testeri nechapou ze dropujou do slozky jenom kdyz maji kurzor NA ni a ne POD ni
                    dragSource.parent = newTree[destinationIndex - 1].id;
                    newTree.splice(destinationIndex, 0, dragSource);
                    //filter je z důvodu opravy duplikování, map je k odebrání nežádoucí cycklické reference (jinak padá stringify na treeData)
                    // @ts-ignore
                    newTree = newTree.filter((item, index) => item.id !== dragSource.id || index === destinationIndex).map(({ref, ...rest}) => rest);
                    setTreeData(newTree);
                }
            }
        },
        [externalData],
    );

    const handleAddSkupina = () => {
        Modal.show(({onClose}) => (
            <CiselnikValueModal
                isEdit={false}
                onSubmit={data => {
                    setTreeData([
                        ...treeData,
                        {
                            //id zacinajici temp- je nutne pred submitem čistit => je pouze pro klienta
                            id: `temp-${uuidv4()}`,
                            parent: 0,
                            text: data.nazev,
                            droppable: true,
                        },
                    ]);
                    onClose();
                }}
                onClose={onClose}
            />
        ));
    };

    useEffect(() => {
        const diskriminatorAtributItem = {
            id: diskriminatorAtribut?.id,
            parent: 0,
            text: diskriminatorAtribut?.popisek ?? '<neznámý atribut>',
            droppable: false,
        };

        // Přidání diskriminatorAtribut do poslední existující skupiny
        if (diskriminatorAtribut) {
            const existingNode = treeData.find(item => item.id === diskriminatorAtributItem.id);
            if (!existingNode) {
                const lastGroup = treeData
                    .slice()
                    .reverse()
                    .find(item => item.droppable);
                if (lastGroup) {
                    addItem({...diskriminatorAtributItem, parent: lastGroup.id});
                }
            }
        }
    }, [values.typ]);

    /**
     * Removes an item from the treeData state and adds it to the externalData state.
     * @param {NodeModel} node - The node to remove.
     */
    const removeItem = (node: NodeModel): void => {
        const newTreeData: NodeModel[] = [];
        const newExternalData: NodeModel[] = [...externalData];

        for (const item of treeData) {
            if (item.id === node.id || item.parent === node.id) {
                newExternalData.push(item);
            } else {
                newTreeData.push(item);
            }
        }

        setTreeData(newTreeData);
        setExternalData(newExternalData.filter(item => !item.droppable));
    };

    /**
     * Přidá atribut do stromu na poslední pozici ve skupině.
     * @param node NodeModel - Node, který má být přidán .
     */
    const addItem = (node: NodeModel): void => {
        // Najdeme poslední uzel v treeData, jehož parent je 0
        const lastNodeWithParentZero = [...treeData].reverse().find(item => item.parent === 0);

        // Pokud takový uzel neexistuje, nebudeme pokračovat
        if (!lastNodeWithParentZero) {
            console.error('Nelze přidat uzel, protože neexistuje uzel s parent 0');
            return;
        }

        // Vytvoříme nový uzel s parent nastaveným na id posledního uzlu s parent 0
        const newNode = {
            ...node,
            parent: lastNodeWithParentZero.id,
        };

        // Přidáme newNode na konec treeData
        const newTreeData: NodeModel[] = [...treeData, newNode];
        // Odebereme uzel z externalData
        const newExternalData: NodeModel[] = externalData.filter(item => item.id !== node.id);

        setTreeData(newTreeData);
        setExternalData(newExternalData);
    };

    /**
     * @param node NodeModel - Node, který má být přesunut .
     * @param atIndex number - Cílový index, na který má být node přesunut.
     */
    const moveItem = (node: NodeModel, atIndex: number) => {
        // Najde index node v treeData
        const index = treeData.findIndex(item => item.id === node.id);

        if (index === -1) return;

        // Najde všechny uzly se stejným rodičem
        const sameParentItems = treeData.filter(item => item.parent === node.parent);

        // Najde index node v sameParentItems
        const sameParentIndex = sameParentItems.findIndex(item => item.id === node.id);

        // Pokud zvyšujeme pozici, vyměníme node s následujícím uzlem v treeData
        if (atIndex > sameParentIndex && index < treeData.length - 1) {
            [treeData[index], treeData[index + 1]] = [treeData[index + 1], treeData[index]];
        }
        // Pokud snižujeme pozici, vyměníme node s předchozím uzlem v treeData
        else if (atIndex < sameParentIndex && index > 0) {
            [treeData[index], treeData[index - 1]] = [treeData[index - 1], treeData[index]];
        }

        setTreeData([...treeData]);
    };

    const changeItem = (node: NodeModel) => {
        Modal.show(({onClose}) => (
            <CiselnikValueModal
                isEdit={true}
                initialValues={{nazev: node.text}}
                onSubmit={data => {
                    setTreeData([...treeData.filter(item => item.id !== node.id), {...node, text: data.nazev}]);
                    onClose();
                }}
                onClose={onClose}
            />
        ));
    };

    // Všechna ID uzlů ve stromu
    const allNodeIds = useMemo(() => treeData.map(node => node.id), [treeData]);

    return (
        <Row>
            <Col>
                <Card
                    mode={'edit'}
                    title={'Formulář akce'}
                    className={'h-100'}
                    extra={
                        // Omezení na založení pouze jedné skupiny, dokud se nerozhodne jinak
                        <Button style={'link'} disabled={treeData.length > 0} onClick={handleAddSkupina}>
                            Přidat skupinu
                        </Button>
                    }
                >
                    <Block>
                        <DndProvider backend={MultiBackend} options={getBackendOptions()}>
                            <div className={'h-100 tree-wrapper'}>
                                {treeData.length === 0 && <NoData />}
                                <Tree
                                    tree={treeData}
                                    initialOpen={allNodeIds}
                                    rootId={0}
                                    extraAcceptTypes={[NativeTypes.TEXT]}
                                    render={(node, {onToggle}) => (
                                        <CustomNode
                                            node={node}
                                            onToggle={onToggle}
                                            removeItem={removeItem}
                                            changeItem={changeItem}
                                            moveItem={moveItem}
                                            treeData={treeData}
                                            diskriminatorAtributId={diskriminatorAtribut?.id}
                                        />
                                    )}
                                    dragPreviewRender={monitorProps => <CustomDragPreview monitorProps={monitorProps} />}
                                    onDrop={handleOrderChange}
                                    classes={{
                                        root: 'treeRoot',
                                        draggingSource: 'draggingSource',
                                        placeholder: 'placeholderContainer',
                                    }}
                                    sort={false}
                                    insertDroppableFirst={false}
                                    canDrop={(tree, {dragSource, dropTargetId, dropTarget, destinationIndex}) => {
                                        if (dragSource?.droppable && dropTargetId === 0) {
                                            //presouvame slozku v ramci prvni urovne
                                            return true;
                                        } else if (!dragSource?.droppable) {
                                            //presouvame atributy
                                            if (dropTargetId !== 0 || destinationIndex !== 0) {
                                                //atribut přesouvám do složky
                                                return true;
                                            }
                                        }
                                        return false;
                                    }}
                                    dropTargetOffset={16}
                                    placeholderRender={(node, {depth}) => <Placeholder node={node} depth={depth} />}
                                />
                            </div>
                        </DndProvider>
                    </Block>
                </Card>
            </Col>
            <Col>
                <Card mode={'edit'} title={'Dostupné atributy vrstvy'} className={'h-100'}>
                    <Block>
                        <Space vertical>
                            {externalData.map(node => (
                                <ExternalNode key={node.id} node={node} diskriminatorAtributId={diskriminatorAtribut?.id} addItem={addItem} />
                            ))}
                            {externalData.length === 0 && <NoData />}
                        </Space>
                    </Block>
                </Card>
            </Col>
        </Row>
    );
};

export default TreeActions;
